/*
  IA PDP Gift buy — <ia-pdp-gift-buy> (blocks/_ia-pdp-gift-buy.liquid).

  Closed, only the "Send as a gift" CTA shows. Opening sets [data-gift-open] on
  the host; ia-pdp-gift-buy.css reveals the recipient fields and the real
  add-to-bag button from there.

  The message limit is soft — the design has an over-limit warning state, so
  nothing caps typing. A capture-phase submit guard stops the add instead: it
  runs before ia-product-buy.js's own submit handler on the form.

  Prices are synced here rather than through [data-re-render] — swapping the
  block wholesale on variant change would wipe a half-typed message.
*/
import { EVENTS, subscribe } from 'ia-util-pubsub'

/* Shopify caps scheduled gift-card delivery at 90 days out. */
const SEND_ON_MAX_DAYS = 90

const toDateValue = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-')

if (!customElements.get('ia-pdp-gift-buy')) {
  class IaPdpGiftBuy extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-gift-trigger]')
      this.email = this.querySelector('[data-gift-email]')
      this.recipientName = this.querySelector('[data-gift-name]')
      this.message = this.querySelector('[data-gift-message]')
      this.counter = this.querySelector('[data-gift-counter]')
      this.offsetInput = this.querySelector('[data-gift-offset]')
      this.dateRow = this.querySelector('[data-gift-date-row]')
      this.date = this.querySelector('[data-gift-date]')

      this.charLimit = Number(this.dataset.charLimit) || 200
      this.counterSuffix = this.dataset.counterSuffix || ''

      this.trigger?.addEventListener('click', this.open.bind(this))
      this.message?.addEventListener('input', this.renderCounter.bind(this))
      this.date?.addEventListener('change', this.renderDate.bind(this))
      this.querySelector('[data-gift-date-button]')?.addEventListener('click', this.pickDate.bind(this))
      this.querySelectorAll('[data-gift-mode]').forEach((mode) => {
        mode.addEventListener('change', this.renderMode.bind(this))
      })
      this.addEventListener('submit', this.guardSubmit.bind(this), true)
      this.addEventListener('submit', this.armReset.bind(this))

      this.unsubscribeVariantChange = subscribe(EVENTS.variantChange, this.syncPrices.bind(this))

      /* Browsers restore field values on back-navigation — resync from them. */
      this.renderCounter()
      this.renderMode()
    }

    disconnectedCallback() {
      this.unsubscribeVariantChange?.()
      this.disarmReset()
    }

    get sectionId() {
      return this.getAttribute('section-id')
    }

    get productId() {
      return this.getAttribute('product-id')
    }

    open() {
      this.setAttribute('data-gift-open', '')
      this.trigger?.setAttribute('aria-expanded', 'true')

      /* Lets Shopify read "Send on" in the buyer's timezone, not the shop's. */
      if (this.offsetInput) this.offsetInput.value = String(new Date().getTimezoneOffset())

      this.setDateBounds()
      this.email?.focus()
    }

    setDateBounds() {
      if (!this.date) return
      const today = new Date()
      const latest = new Date(today)
      latest.setDate(latest.getDate() + SEND_ON_MAX_DAYS)
      this.date.min = toDateValue(today)
      this.date.max = toDateValue(latest)
    }

    renderCounter() {
      if (!this.message || !this.counter) return
      const { length } = this.message.value
      const isOver = length > this.charLimit
      const count = length ? `${length}/${this.charLimit}` : `${this.charLimit}`

      this.counter.textContent = `${count} ${this.counterSuffix}`.trim()
      this.counter.classList.toggle('is-over', isOver)
      this.message.classList.toggle('is-over', isOver)
      this.message.setAttribute('aria-invalid', String(isOver))
    }

    renderMode() {
      if (!this.date || !this.dateRow) return
      const isScheduled = this.querySelector('[data-gift-mode]:checked')?.value === 'date'

      this.dateRow.hidden = !isScheduled
      this.date.disabled = !isScheduled
      this.date.required = isScheduled
      if (!isScheduled) this.date.value = ''
      this.renderDate()
    }

    renderDate() {
      this.date?.classList.toggle('is-empty', !this.date.value)
    }

    pickDate() {
      if (!this.date || this.date.disabled) return
      this.date.focus()
      try {
        this.date.showPicker?.()
      } catch {
        /* Older browsers open their own picker on focus. */
      }
    }

    /* Runs ahead of ia-product-buy.js's handler on the form — stopping
       propagation here keeps an invalid gift out of the cart. */
    guardSubmit(event) {
      if (!this.hasAttribute('data-gift-open')) return

      const invalidField = [...this.querySelectorAll('[data-gift-required]')].find(
        (field) => !field.disabled && !field.checkValidity()
      )
      const isOverLimit = this.message ? this.message.value.length > this.charLimit : false
      if (!invalidField && !isOverLimit) return

      event.preventDefault()
      event.stopPropagation()

      if (invalidField) {
        invalidField.focus()
        invalidField.reportValidity()
      } else {
        this.message.focus()
      }
    }

    /* The add is async and cartChanged names no source, so a submit arms a
       one-shot listener — the gift is only cleared once it's really in the bag. */
    armReset() {
      if (!this.hasAttribute('data-gift-open')) return

      this.disarmReset()
      this.unsubscribeCartChanged = subscribe(EVENTS.cartChanged, () => {
        this.disarmReset()
        this.reset()
      })
    }

    disarmReset() {
      this.unsubscribeCartChanged?.()
      this.unsubscribeCartChanged = null
    }

    /* Empties the recipient fields so the next gift starts fresh. */
    reset() {
      if (this.email) this.email.value = ''
      if (this.recipientName) this.recipientName.value = ''
      if (this.message) this.message.value = ''

      const sendNow = this.querySelector('[data-gift-mode][value="now"]')
      if (sendNow) sendNow.checked = true

      /* Clears and re-disables the date field. */
      this.renderMode()
      this.renderCounter()
    }

    /* Swap only the price nodes so the open form keeps what the shopper typed. */
    syncPrices(event) {
      const { html, sectionId, productId } = event.detail
      if (!html || sectionId != this.sectionId || productId != this.productId) return

      const next = html.querySelector(`#${CSS.escape(this.id)}`)
      if (!next) return

      const nextPrices = next.querySelectorAll('.ia-price')
      this.querySelectorAll('.ia-price').forEach((price, index) => {
        if (nextPrices[index]) price.outerHTML = nextPrices[index].outerHTML
      })
    }
  }

  customElements.define('ia-pdp-gift-buy', IaPdpGiftBuy)
}
